import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildStatusDeltaMap } from '@/features/metrics/components/statusMetricCards';
import type { EntityTrendsPageConfig, EntityStatusKey } from './useEntityTrendsPage.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';

type UseEntityTrendsSummaryInput<TEntityItem, TSummary extends { total: number }> = {
  config: EntityTrendsPageConfig<TEntityItem, TSummary>;
  isLatestMode: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  accessToken: string | null;
  summaryInput: GridQueryInput;
  previousSummaryInput: GridQueryInput | null;
  summaryFiltersKey: string;
  previousSummaryFiltersKey: string;
  showDelta: boolean;
};

type UseEntityTrendsSummaryResult<TSummary extends { total: number }> = {
  summary: TSummary | null;
  previousSummary: TSummary | null;
  counts: Partial<Record<EntityStatusKey, number>>;
  previousCounts: Partial<Record<EntityStatusKey, number>>;
  deltas: ReturnType<typeof buildStatusDeltaMap> | undefined;
  summaryIsFetching: boolean;
  summaryIsLoading: boolean;
};

export const useEntityTrendsSummary = <TEntityItem, TSummary extends { total: number }>({
  config,
  isLatestMode,
  selectedProjectId,
  selectedOrganisationId,
  accessToken,
  summaryInput,
  previousSummaryInput,
  summaryFiltersKey,
  previousSummaryFiltersKey,
  showDelta,
}: UseEntityTrendsSummaryInput<TEntityItem, TSummary>): UseEntityTrendsSummaryResult<TSummary> => {
  const summaryQuery = useQuery<TSummary>({
    queryKey: isLatestMode
      ? config.summaryKey(
          selectedProjectId ?? null,
          selectedOrganisationId ?? null,
          summaryFiltersKey,
        )
      : config.executionSummaryKey(
          selectedProjectId ?? null,
          selectedOrganisationId ?? null,
          summaryFiltersKey,
        ),
    enabled: Boolean(accessToken),
    queryFn: async () =>
      isLatestMode
        ? config.queryLatestSummary(
            selectedProjectId,
            selectedOrganisationId ?? undefined,
            summaryInput,
            accessToken,
          )
        : config.queryExecutionSummary(
            selectedProjectId,
            selectedOrganisationId ?? undefined,
            summaryInput,
            accessToken,
          ),
  });
  const previousSummaryQuery = useQuery<TSummary>({
    queryKey: config.executionSummaryKey(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && !isLatestMode,
    queryFn: async () =>
      config.queryExecutionSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? summaryInput,
        accessToken,
      ),
  });

  const summary = summaryQuery.data ?? null;
  const previousSummary = !isLatestMode ? (previousSummaryQuery.data ?? null) : null;
  const counts = useMemo(() => config.buildCounts(summary), [config, summary]);
  const previousCounts = useMemo(
    () => config.buildCounts(previousSummary),
    [config, previousSummary],
  );
  const deltas = useMemo(
    () => (showDelta && previousSummary ? buildStatusDeltaMap(counts, previousCounts) : undefined),
    [counts, previousCounts, previousSummary, showDelta],
  );

  return {
    summary,
    previousSummary,
    counts,
    previousCounts,
    deltas,
    summaryIsFetching: summaryQuery.isFetching,
    summaryIsLoading: summaryQuery.isLoading,
  };
};
